import React, { useState, useMemo, useRef, useCallback, useEffect, useContext, createContext } from 'react'
import { readTheme, saveTheme } from '../theme/frost'
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import styles from './StudentLessonView.module.scss'
import UserContext from '../context/UserContext'
import {
  GET_MEETING_WITH_CONTENT_QUERY, RECORD_PRACTICE_MUTATION, RECORD_LESSON_RESULTS_MUTATION, MY_LESSON_PROGRESS_QUERY, MY_SUBMISSION_QUERY, SUBMIT_SPEAKING_ANSWERS_MUTATION,
  MY_LESSON_WORK_QUERY, SAVE_LESSON_WORK_MUTATION, DELETE_LESSON_WORK_MUTATION, MY_TEACHER_LINKS_QUERY,
  LESSON_TRANSLATION_QUERY, TRANSLATE_LESSON_MUTATION,
} from '../query/gqlQueries'
import useLearnerContext from '../hooks/useLearnerContext'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import useSpeechToText from '../speech/useSpeechToText'
import languages from '../languages'
import { streamOllamaGenerate } from '../ollama'
import { celebrate, popScale, shake } from './juice'
import SpokenText from './SpokenText'
import { WordLookupProvider, useWordLookup, normWord } from './WordLookup'

import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'

// ── matching helpers ─────────────────────────────────────────────────────────
const norm = (s) => String(s || '').normalize('NFC').toLocaleLowerCase('tr-TR').replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[b[i], b[j]] = [b[j], b[i]] } return b }
const parseObj = (raw) => {
  let s = String(raw || '').replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = s.indexOf('{'), b = s.lastIndexOf('}')
  if (a >= 0 && b > a) s = s.slice(a, b + 1)
  try { return JSON.parse(s) } catch { return null }
}

const KIND_META = {
  concept: { label: 'Learn' },
  vocab: { label: 'Vocabulary' },
  paradigm: { label: 'Table' },
  patterns: { label: 'Patterns' },
  practice: { label: 'Practice' },
  reading: { label: 'Reading & comprehension' },
  dialogue: { label: 'Mini dialogue' },
  speaking: { label: 'Speaking' },
}
// Section accents rotate like the numbered badges in the workbooks.
const ACCENTS = ['olive', 'terra', 'mustard', 'sage']
const accentAt = (i) => ACCENTS[i % ACCENTS.length]

// Dark by default; a learner's explicit choice (either way) is remembered per device.
// Day or night: the computer's, until chosen — one choice for the whole site (theme/frost.js).

// Shared per-lesson bits (language, voice, rate, model access) for the blocks.
const LessonCtx = createContext({})
const useLesson = () => useContext(LessonCtx)

// Character-based scripts: typed answers are built from characters, not words.
const CJK = new Set(['kor', 'jpn', 'cmn', 'tha'])

// The languages lessons can be explained in — the same as src/languages.js.
const SUPPORTED = new Set(['eng', 'rus', 'ita', 'por', 'tur', 'spa', 'swe', 'nld', 'cmn', 'vie', 'kor', 'ara', 'jpn', 'ell', 'deu', 'fra'])
const OWN_NAMES = { eng: 'English', rus: 'Русский', ita: 'Italiano', por: 'Português', tur: 'Türkçe', spa: 'Español', swe: 'Svenska', nld: 'Nederlands', cmn: '中文', vie: 'Tiếng Việt', kor: '한국어', ara: 'العربية', jpn: '日本語', ell: 'Ελληνικά', deu: 'Deutsch', fra: 'Français' }

// Asks the server for the lesson explained in `own`, starting the translation
// if nobody has needed it yet, and checks back until it's ready. Anything that
// goes wrong just leaves the lesson as written.
function useLessonInOwnLanguage(meetingId, lesson, own) {
  const client = useApolloClient()
  const [translated, setTranslated] = useState(null)
  const [translating, setTranslating] = useState(false)
  const [showOriginal, setShowOriginal] = useState(false)
  const source = (lesson && lesson.instructionLang) || 'eng'
  useEffect(() => {
    setTranslated(null)
    if (!meetingId || !lesson || own === source) return undefined
    let stop = false
    const ask = async (start) => {
      const res = start
        ? await client.mutate({ mutation: TRANSLATE_LESSON_MUTATION, variables: { m: String(meetingId), l: own } })
        : await client.query({ query: LESSON_TRANSLATION_QUERY, variables: { m: String(meetingId), l: own }, fetchPolicy: 'network-only' })
      return start ? res.data.translateLesson : res.data.lessonTranslation
    }
    ;(async () => {
      try {
        let state = await ask(false)
        if (state.status === 'original') return
        if (state.status === 'none' || state.status === 'failed') state = await ask(true)
        const t0 = Date.now()
        while (!stop && state.status === 'generating' && Date.now() - t0 < 20 * 60 * 1000) {
          setTranslating(true)
          await new Promise(r => setTimeout(r, 5000))
          state = await ask(false)
        }
        if (!stop && state.status === 'ready' && state.lessonData) setTranslated(JSON.parse(state.lessonData))
      } catch (e) {
        // Not deployed yet, or a model hiccup: the lesson as written is fine.
      } finally {
        if (!stop) setTranslating(false)
      }
    })()
    return () => { stop = true }
  }, [client, meetingId, lesson, own, source])
  return { translated, translating, showOriginal, setShowOriginal }
}

// Its own title, or — when the lesson gave none (older ones said "Section") —
// a name from what it contains.
// How well I know this lesson (server: lib/proficiency.js) — whole sentences
// count 3×, gap-fills 1.5×, words and choices 1×, and only the latest answer to
// each item counts. Every checked answer is sent at once, so progress saves as
// you go; anything that couldn't be sent waits in localStorage and goes with
// the next answer, or when the lesson closes.
function useLessonProficiency(meetingId) {
  const id = meetingId ? String(meetingId) : null
  const storeKey = id ? `lessonResults.unsent.${id}` : null
  const { data } = useQuery(MY_LESSON_PROGRESS_QUERY, { variables: { ids: id ? [id] : [] }, skip: !id, fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const [send] = useMutation(RECORD_LESSON_RESULTS_MUTATION)
  const [pct, setPct] = useState(null)
  useEffect(() => {
    const row = data?.myLessonProgress?.find(r => String(r.meetingId) === id)
    if (id) setPct(p => (p == null ? (row ? row.pct : 0) : p))
  }, [data, id])
  const readUnsent = useCallback(() => { try { return JSON.parse(localStorage.getItem(storeKey) || '{}') } catch { return {} } }, [storeKey])
  const writeUnsent = useCallback((o) => { try { Object.keys(o).length ? localStorage.setItem(storeKey, JSON.stringify(o)) : localStorage.removeItem(storeKey) } catch {} }, [storeKey])
  const flushResults = useCallback(async () => {
    if (!id) return
    const unsent = readUnsent()
    const results = Object.entries(unsent).map(([key, correct]) => ({ key, correct: !!correct }))
    if (!results.length) return
    try {
      const res = await send({ variables: { meetingId: id, results } })
      const now = readUnsent()
      for (const r of results) if (now[r.key] === r.correct) delete now[r.key]
      writeUnsent(now)
      const p = res?.data?.recordLessonResults?.pct
      if (typeof p === 'number') setPct(p)
    } catch { /* kept for the next answer or the close */ }
  }, [id, readUnsent, writeUnsent, send])
  const lessonResult = useCallback((key, correct) => {
    if (!id || !key) return
    writeUnsent({ ...readUnsent(), [String(key)]: !!correct })
    flushResults()
  }, [id, readUnsent, writeUnsent, flushResults])
  // Whatever is still waiting from an earlier visit goes as soon as the lesson opens.
  useEffect(() => { flushResults() }, [flushResults])
  return { pct, lessonResult, flushResults }
}

const EXERCISE_LABELS = { fill: 'Fill in the blank', choose: 'Choose the answer', translate: 'Translate', match: 'Match the pairs' }
const sectionTitle = (s) => (s.title && s.title !== 'Section' ? s.title
  : s.exercise ? (EXERCISE_LABELS[s.exercise.type] || 'Practice') : (KIND_META[s.kind]?.label || 'Practice'))

export default function StudentLessonView({ lesson: asWritten, voiceSpeed, onClose, meetingId, onComplete, completed, homework = false }) {
  const { username } = useContext(UserContext)
  // The lesson explained in the learner's own language — translated on the
  // Mac Studio the first time anyone needs it, then kept. Until it's ready,
  // and whenever they ask, the lesson as written.
  const { native } = useLearnerContext(username)
  const own = (native.find(n => SUPPORTED.has(n.code)) || {}).code || 'eng'
  const { translated, translating, showOriginal, setShowOriginal } = useLessonInOwnLanguage(meetingId, asWritten, own)
  const lesson = translated && !showOriginal ? translated : asWritten
  const [recordPractice] = useMutation(RECORD_PRACTICE_MUTATION)
  const { pct: known, lessonResult, flushResults } = useLessonProficiency(meetingId)
  // When this lesson is opened from a SAVED class, resolve its vocab/sentences
  // back to real Word/Sentence node ids by text, so reviewing a section can
  // feed spaced repetition. Ephemeral (import-preview) lessons carry no id and
  // simply don't record.
  const idMapRef = useRef(new Map())
  const [fetchContent, { data: contentData }] = useLazyQuery(GET_MEETING_WITH_CONTENT_QUERY)
  useEffect(() => { if (meetingId) fetchContent({ variables: { identity: meetingId } }) }, [meetingId, fetchContent])
  useEffect(() => {
    const c = contentData?.getMeetingWithContent
    if (!c) return
    const m = new Map()
    ;(c.words || []).forEach(w => { if (w.source?.text) m.set(norm(w.source.text), { id: w.source.id, itemType: 'Word' }) })
    ;(c.sentences || []).forEach(s => { if (s.source?.text) m.set(norm(s.source.text), { id: s.source.id, itemType: 'Sentence' }) })
    idMapRef.current = m
  }, [contentData])

  // Every answer the learner gives ANYWHERE in the lesson, not just the
  // speaking chapter — practice exercises were previously answered and thrown
  // away, so a teacher saw only the last page of a lesson's worth of work.
  //
  // Keyed by the prompt rather than by position: re-answering replaces instead
  // of duplicating, and a saved submission can be matched back when the lesson
  // is reopened even though the item order differs between chapters.
  const workRef = useRef(new Map())
  const recordAnswer = useCallback((prompt, text) => {
    const k = String(prompt || '').trim()
    if (k) workRef.current.set(k, String(text == null ? '' : text))
  }, [])
  const [submitWork] = useMutation(SUBMIT_SPEAKING_ANSWERS_MUTATION)
  const [savedAt, setSavedAt] = useState(null)
  const [saveErr, setSaveErr] = useState('')
  // Only HOMEWORK is sent anywhere. A lesson opened from the Skill Tree, from
  // Meetings, or as an import preview has no teacher waiting for it — and
  // submitting one anyway made that lesson read "Submitted" on the day a
  // teacher later assigned it, before the student had touched it.
  const canSave = !!(homework && meetingId && username)
  // The whole submission is replaced each time, so always send everything.
  const saveWork = useCallback(async () => {
    if (!canSave || workRef.current.size === 0) return null
    const answers = [...workRef.current.entries()].map(([prompt, text], index) => ({ index, prompt, text }))
    try {
      const res = await submitWork({ variables: { meetingId, answers } })
      const sub = res.data?.submitSpeakingAnswers
      setSavedAt(sub?.updatedAt || sub?.submittedAt || new Date().toISOString())
      setSaveErr('')
      return sub
    } catch (e) { setSaveErr(e.message || 'Could not save.'); throw e }
  }, [canSave, meetingId, submitWork])

  // A Skill Tree lesson: nothing is sent while working. On finishing, the
  // learner chooses whether to keep their answers and which of their teachers
  // for this language may see them — kept apart from homework, so a teacher
  // who later assigns the lesson still sees it as not started.
  const ownLesson = !!(onComplete && meetingId && username && !homework)
  const { data: workData, refetch: refetchWork } = useQuery(MY_LESSON_WORK_QUERY, { variables: { meetingId }, skip: !ownLesson, fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const savedWork = workData?.myLessonWork || null

  // What was saved before, so reopening a lesson shows the work back.
  const { data: subData } = useQuery(MY_SUBMISSION_QUERY, { variables: { meetingId }, skip: !canSave, fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const savedAnswers = useMemo(() => {
    const m = new Map()
    ;((ownLesson ? savedWork?.answers : subData?.mySubmission?.answers) || []).forEach(a => m.set(String(a.prompt || '').trim(), a.text || ''))
    return m
  }, [subData, savedWork, ownLesson])
  useEffect(() => { savedAnswers.forEach((text, prompt) => { if (!workRef.current.has(prompt)) workRef.current.set(prompt, text) }) }, [savedAnswers])
  // Memoised: this goes into the lesson context, and a fresh object each render
  // would rebuild the context and re-render every block on every keystroke.
  const teacherNote = useMemo(() => {
    const sub = ownLesson ? savedWork : subData?.mySubmission
    return sub ? { comment: sub.comment, reviewedAt: sub.reviewedAt, by: sub.commentBy } : null
  }, [subData, savedWork, ownLesson])

  const recordText = useCallback((text, isCorrect = true) => {
    const hit = idMapRef.current.get(norm(text))
    if (hit && username) recordPractice({ variables: { itemId: hit.id, itemType: hit.itemType, isCorrect } }).catch(() => {})
  }, [username, recordPractice])
  const recordSection = useCallback((section) => {
    if (!section) return
    ;(section.words || []).forEach(w => recordText(w.tr, true))
    ;(section.sentences || []).forEach(s => recordText(s.tr, true))
  }, [recordText])

  const langCode = lesson?.language || 'tur'
  const langName = lesson?.languageName || languages[langCode] || 'Turkish'
  const voice = voices[langCode] || voices['eng']
  // When opened from the import preview there's no voiceSpeed prop — fall back to
  // a calm, learner-friendly 0.9 rather than letting an undefined rate race.
  const rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 0.9
  const say = useCallback((t) => t && speak(t, voice, rate), [voice, rate])

  // The lesson's own vocabulary + glossary answer word taps instantly; the
  // model only fills in what the lesson doesn't already define.
  const localDict = useMemo(() => {
    const m = new Map()
    ;(lesson?.sections || []).forEach(s => (s.words || []).forEach(w => { if (w.tr && w.en) m.set(normWord(w.tr), w.en) }))
    ;(lesson?.reading?.glossary || []).forEach(g => { if (g.tr && g.en) m.set(normWord(g.tr), g.en) })
    return m
  }, [lesson])

  // Small helper for the on-demand AI bits (hints, translations, explanations).
  const askModel = useCallback(async (prompt, { json = false, max = 300, model = 'gemma4:26b' } = {}) => {
    const raw = await streamOllamaGenerate({ body: { model, prompt, ...(json ? { format: 'json' } : {}), options: { temperature: 0.3, num_predict: max } } })
    return json ? parseObj(raw) : String(raw || '').trim()
  }, [])

  // Light (the workbook look) / dark (inverted) — remembered per device.
  const [theme, setTheme] = useState(readTheme)
  useEffect(() => {
    const follow = (e) => setTheme(e.detail || readTheme())
    window.addEventListener('frost-theme', follow)
    return () => window.removeEventListener('frost-theme', follow)
  }, [])
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    saveTheme(next)
    return next
  })

  const sections = lesson?.sections || []
  const chapters = useMemo(() => {
    const list = sections.map((s, i) => ({ id: `s${i}`, title: sectionTitle(s), kind: s.kind }))
    if (lesson?.reading) list.push({ id: 'reading', title: lesson.reading.title || 'Reading', kind: 'reading' })
    if (lesson?.dialogue) list.push({ id: 'dialogue', title: lesson.dialogue.title || 'Dialogue', kind: 'dialogue' })
    if (lesson?.speaking?.length) list.push({ id: 'speaking', title: 'Speaking', kind: 'speaking' })
    return list
  }, [sections, lesson])

  const [active, setActive] = useState(0)
  const [done, setDone] = useState({}) // chapterId -> true
  const markDone = useCallback((id) => {
    setDone(d => (d[id] ? d : { ...d, [id]: true }))
    // Finishing a chapter is the natural save point: the learner may never
    // reach the speaking section, and their exercise work should still arrive.
    saveWork().catch(() => { /* surfaced by saveErr */ })
  }, [saveWork])
  const progress = Math.round((Object.keys(done).length / Math.max(1, chapters.length)) * 100)
  const allDone = chapters.length > 0 && Object.keys(done).length >= chapters.length

  const goNext = () => setActive(a => Math.min(chapters.length - 1, a + 1))
  const goPrev = () => setActive(a => Math.max(0, a - 1))

  // On the last page, or once every chapter is done, the way out is "save and
  // close" in the corner people already look at — not a greyed-out Next and a
  // small X at the top. It glows once the whole lesson is done: a hint, not a nag.
  const isLast = chapters.length > 0 && active === chapters.length - 1
  const showFinish = isLast || allDone
  const [finishing, setFinishing] = useState(false)
  const [finishErr, setFinishErr] = useState('')
  const finishLabel = canSave ? 'Save & close ✓' : ownLesson ? 'Save & complete ✓' : (onComplete && !completed ? 'Complete & close ✓' : 'Finish & close ✓')
  const [choosing, setChoosing] = useState(false)   // the keep / share choice for a Skill Tree lesson
  const answeredNow = () => [...workRef.current.entries()].filter(([, v]) => String(v || '').trim())
  const finish = async () => {
    if (ownLesson && answeredNow().length) { setChoosing(true); return }
    setFinishErr(''); setFinishing(true)
    try {
      await saveWork()
    } catch (e) {
      // Never close over unsaved work: say so and stay on the page.
      setFinishErr(e.message || 'Could not save — your work is still here.')
      setFinishing(false)
      return
    }
    setFinishing(false)
    // The Skill Tree's onComplete marks the topic done AND closes the lesson,
    // so calling onClose as well would close twice.
    if (onComplete) await onComplete()
    else onClose()
  }
  const ch = chapters[active]
  const accent = accentAt(active)

  const ctxValue = useMemo(() => ({ lang: langCode, langName, voice, rate, say, askModel, meetingId, username, recordAnswer, saveWork, savedAnswers, savedAt, saveErr, teacherNote, canSave, lessonResult }), [langCode, langName, voice, rate, say, askModel, meetingId, username, recordAnswer, saveWork, savedAnswers, savedAt, saveErr, teacherNote, canSave, lessonResult])

  return (
    <LessonCtx.Provider value={ctxValue}>
      <WordLookupProvider lang={langCode} langName={langName} localDict={localDict} voice={voice} rate={rate}>
        <div className={`${styles.view} ${theme === 'dark' ? styles.dark : styles.light}`}>
          <div className={styles.topBtns}>
            <button className={styles.iconBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </button>
            <button className={styles.iconBtn} onClick={() => { flushResults(); onClose() }} title="Close"><CloseIcon /></button>
          </div>

          <header className={styles.header}>
            <div className={styles.headRow}>
              <h1 className={styles.title}>{lesson?.title || 'Lesson'}</h1>
              <span className={styles.levelPill}>{lesson?.level || 'A0'}</span>
            </div>
            {lesson?.goal && <div className={styles.goalBox}><strong>Goal:</strong> {lesson.goal}</div>}
            {translating && <div className={styles.savedNote}>Translating the explanations into {OWN_NAMES[own] || own}… you can start in the meantime.</div>}
            {translated && (
              <div className={styles.savedNote}>
                🌐 {showOriginal ? 'Showing the original' : `Explained in ${OWN_NAMES[own] || own}`} ·{' '}
                <button className={styles.toolBtn} onClick={() => setShowOriginal(v => !v)}>{showOriginal ? `Show in ${OWN_NAMES[own] || own}` : 'Show original'}</button>
              </div>
            )}
            {ownLesson && savedWork && (
              <div className={styles.savedNote}>
                ✓ Your answers from {new Date(savedWork.updatedAt || savedWork.savedAt).toLocaleDateString()} are saved
                {savedWork.sharedWith?.length ? ` and shared with ${savedWork.sharedWith.join(', ')}` : ' — only you can see them'}.
              </div>
            )}
            {ownLesson && teacherNote?.comment && (
              <div className={styles.teacherNote}><b>{teacherNote.by || 'Teacher'}</b> {teacherNote.comment}</div>
            )}
            {known != null && (
              <div className={styles.savedNote} title="Whole sentences count most, then gap-fills, then single words — only your latest answer to each counts.">
                📈 <b>{known}% known</b>{known < 90 ? ' — getting the full sentences right counts most' : ' — well acquainted'}
              </div>
            )}
            <div className={styles.progressTrack}><div className={styles.progressFill} style={{ width: `${progress}%` }} /></div>
            <hr className={styles.headerRule} />
          </header>

          <nav className={styles.chapterNav}>
            {chapters.map((c, i) => (
              <button key={c.id} data-accent={accentAt(i)}
                className={`${styles.chapterTab} ${i === active ? styles.chapterTabActive : ''} ${done[c.id] ? styles.chapterTabDone : ''}`}
                onClick={() => setActive(i)}>
                <span className={styles.tabNum}>{done[c.id] ? '✓' : i + 1}</span>
                <span className={styles.chapterName}>{c.title}</span>
              </button>
            ))}
          </nav>

          <main className={styles.stage} key={ch?.id}>
            {ch?.kind === 'reading' ? <ReadingBlock reading={lesson.reading} index={active} accent={accent} onComplete={() => markDone(ch.id)} />
              : ch?.kind === 'dialogue' ? <DialogueBlock dialogue={lesson.dialogue} index={active} accent={accent} onComplete={() => markDone(ch.id)} />
                : ch?.kind === 'speaking' ? <SpeakingBlock prompts={lesson.speaking} keys={lesson.speakingKeys} index={active} accent={accent} onComplete={() => markDone(ch.id)} />
                  : <SectionBlock section={sections[active]} index={active} accent={accent} onComplete={() => { recordSection(sections[active]); markDone(ch.id) }} />}
          </main>

          {onComplete && (allDone || completed) && (
            <div className={styles.completeBar}>
              <span className={styles.completeText}>{completed ? '✓ Topic completed' : '🎉 You finished every section'}</span>
            </div>
          )}
          {choosing && (
            <FinishChoice lang={langCode} langName={langName} previous={savedWork} meetingId={meetingId}
              answers={answeredNow().map(([prompt, text], index) => ({ index, prompt, text: String(text).trim() }))}
              onCancel={() => setChoosing(false)}
              onDone={async () => { setChoosing(false); refetchWork(); if (onComplete) await onComplete(); else onClose() }} />
          )}
          <footer className={styles.footer}>
            <button className={styles.navBtn} onClick={goPrev} disabled={active === 0}>← Back</button>
            <span className={styles.footInfo}>
              {finishErr ? <span className={styles.finishErr}>{finishErr}</span> : `${langName} · ${active + 1} / ${chapters.length}`}
            </span>
            {showFinish
              ? (
                <button className={`${styles.navBtn} ${styles.navBtnPrimary} ${allDone ? styles.finishGlow : ''}`} onClick={finish} disabled={finishing}>
                  {finishing ? 'Saving…' : finishLabel}
                </button>
              )
              : <button className={styles.navBtn} onClick={goNext} disabled={chapters.length === 0}>Next →</button>}
          </footer>
        </div>
      </WordLookupProvider>
    </LessonCtx.Provider>
  )
}

// Numbered, colour-coded section header (the workbook's "① Title" badges).
function SectionHead({ index, title, kind }) {
  return (
    <>
      <div className={styles.secHead}>
        <span className={styles.secNum}>{index + 1}</span>
        <h2 className={styles.sectionTitle}>{title}</h2>
      </div>
      <div className={styles.sectionKind}>{KIND_META[kind]?.label || 'Section'}</div>
    </>
  )
}

// ── a teaching / practice section ────────────────────────────────────────────
function SectionBlock({ section, index, accent, onComplete }) {
  const { lang, voice, rate, say } = useLesson()
  const { lookup } = useWordLookup()
  if (!section) return null
  return (
    <div className={styles.section} data-accent={accent}>
      <SectionHead index={index} title={section.title} kind={section.kind} />
      {section.note && <p className={styles.note}>{section.note}</p>}

      {section.table && <ParadigmTable table={section.table} say={say} />}

      {section.words?.length > 0 && (
        <div className={styles.vocabGrid}>
          {section.words.map((w, i) => (
            <button key={i} className={styles.vocabCard} onClick={() => say(w.tr)} title="Tap to hear · ⓘ for details">
              <span className={styles.vocabTr}>{w.tr}</span>
              <span className={styles.vocabEn}>{w.en}</span>
              <span className={styles.vocabInfo} role="button" onClick={(e) => { e.stopPropagation(); lookup(w.tr, e) }}>ⓘ</span>
            </button>
          ))}
        </div>
      )}

      {section.sentences?.length > 0 && (
        <ul className={styles.sentenceList}>
          {section.sentences.map((s, i) => (
            <li key={i} className={styles.sentenceRow}>
              <SpokenText className={styles.sentenceTr} text={s.tr} lang={lang} voice={voice} rate={rate} onWordClick={(w, e) => lookup(w, e, s.tr)} />
              <span className={styles.sentenceEn}>{s.en}</span>
            </li>
          ))}
        </ul>
      )}

      {section.exercise
        ? <Exercise ex={section.exercise} onComplete={onComplete} sectionTitle={section.key || section.title} />
        : <button className={styles.doneBtn} onClick={onComplete}>Mark reviewed ✓</button>}
    </div>
  )
}

function ParadigmTable({ table, say }) {
  const cols = table.columns || []
  return (
    <div className={styles.tableWrap}>
      <table className={styles.paradigm}>
        <thead><tr>{cols.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
        <tbody>
          {(table.rows || []).map((row, r) => (
            <tr key={r}>{row.map((cell, c) => <td key={c} onClick={() => c > 0 && say(cell)}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── exercise runners (auto-graded from the answer key) ───────────────────────
function Exercise({ ex, onComplete, sectionTitle }) {
  const { say, recordAnswer, lessonResult } = useLesson()
  const type = ex?.type || 'translate'
  const items = ex?.items || []
  const [idx, setIdx] = useState(0)
  const [solved, setSolved] = useState({})
  const cardRef = useRef(null)

  const item = items[idx]
  const allSolved = Object.keys(solved).length >= items.length && items.length > 0
  const siblings = useMemo(() => items.map(it => it.a).filter((a, i) => i !== idx), [items, idx])

  const solveCurrent = useCallback((ok, text) => {
    if (ok) {
      const el = cardRef.current
      if (el) { const r = el.getBoundingClientRect(); celebrate(r.left + r.width / 2, r.top + 40, { count: 20, spread: 140 }) }
    }
    setSolved(s => ({ ...s, [idx]: ok ? 'right' : 'seen' }))
    // What the learner actually answered, so the teacher sees their working and
    // not just a tick. `null` means the item had no answer to give (a reveal).
    const it = items[idx]
    // Keyed by the original wording (key) when the lesson has been translated,
    // so answers match the teacher's copy.
    if (text != null && it) {
      recordAnswer(`${sectionTitle || 'Practice'} · ${it.key || it.q}`, text)
      // Counts towards how well the lesson is known, saved as you go.
      if (lessonResult) lessonResult(it.key || it.q, ok)
    }
  }, [idx, items, recordAnswer, sectionTitle, lessonResult])

  const next = () => setIdx(i => Math.min(items.length - 1, i + 1))
  const prev = () => setIdx(i => Math.max(0, i - 1))

  if (type === 'match') return <MatchExercise ex={ex} say={say} onComplete={onComplete} />

  return (
    <div className={styles.exercise} ref={cardRef}>
      <div className={styles.exHead}><span className={styles.exType}>{type}</span><span className={styles.exPrompt}>{ex.prompt}</span></div>

      {item && (
        <div className={styles.exItem}>
          {type === 'choose' && <ChooseItem key={idx} item={item} onResult={solveCurrent} />}
          {(type === 'fill' || type === 'transform' || type === 'translate' || type === 'order') && <TypeItem key={idx} item={item} type={type} siblings={siblings} onResult={solveCurrent} />}
          {type === 'speak' && <SpeakItem key={idx} item={item} say={say} onResult={solveCurrent} />}
        </div>
      )}

      <div className={styles.exNav}>
        <button className={styles.navBtnSm} onClick={prev} disabled={idx === 0}>←</button>
        <span className={styles.exCount}>{idx + 1} / {items.length}</span>
        <button className={styles.navBtnSm} onClick={next} disabled={idx === items.length - 1}>→</button>
      </div>

      {allSolved && <button className={styles.doneBtn} onClick={onComplete}>Finish exercise ✓</button>}
    </div>
  )
}

function ChooseItem({ item, onResult }) {
  const { lang, voice, rate } = useLesson()
  const { lookup } = useWordLookup()
  const [picked, setPicked] = useState(null)
  const options = item.options && item.options.length ? item.options : [item.a]
  return (
    <>
      <div className={styles.exQuestion}>
        <SpokenText text={item.q} lang={lang} voice={voice} rate={rate} showSpeaker={false} onWordClick={(w, e) => lookup(w, e, item.q)} />
      </div>
      <div className={styles.choiceRow}>
        {options.map((o, i) => {
          const isAns = norm(o) === norm(item.a)
          const chosen = picked === i
          const cls = [styles.choiceBtn, picked != null && isAns ? styles.choiceRight : '', chosen && !isAns ? styles.choiceWrong : ''].join(' ')
          return <button key={i} className={cls} disabled={picked != null} onClick={(e) => { setPicked(i); onResult(isAns, o); if (isAns) popScale(e.currentTarget) }}>{o}</button>
        })}
      </div>
    </>
  )
}

// How a typed answer is built from tiles: characters for CJK scripts, words for
// multi-word answers, single letters for one Latin word (helps with ö ğ ç ş ı).
const unitMode = (answer, lang) => (CJK.has(lang) ? 'char' : (/\s/.test(String(answer).trim()) ? 'word' : 'letter'))
const splitUnits = (s, mode) => {
  const str = String(s || '')
  if (mode === 'word') return str.trim().split(/\s+/).map(u => u.replace(/[^\p{L}\p{N}]+$/u, '')).filter(Boolean)
  return Array.from(str).filter(ch => /[\p{L}\p{N}]/u.test(ch))
}
const buildTiles = (answer, siblings, mode) => {
  const base = Array.from(new Set(splitUnits(answer, mode)))
  const pool = Array.from(new Set(siblings.flatMap(a => splitUnits(a, mode)).filter(u => !base.includes(u))))
  const distract = shuffle(pool).slice(0, mode === 'word' ? 2 : 3)
  const tiles = shuffle([...base, ...distract])
  if (mode !== 'word' && /\s/.test(String(answer).trim())) tiles.push(' ')
  return tiles
}

function TypeItem({ item, type, siblings = [], onResult }) {
  const { lang, voice, rate, say } = useLesson()
  const { lookup } = useWordLookup()
  const [val, setVal] = useState('')
  const [state, setState] = useState(null) // right | wrong | revealed
  const [hint, setHint] = useState(0)       // revealed characters of the answer
  const [tilesOn, setTilesOn] = useState(false)
  const inputRef = useRef(null)
  const answer = String(item.a || '')
  const mode = useMemo(() => unitMode(answer, lang), [answer, lang])
  const tiles = useMemo(() => buildTiles(answer, siblings, mode), [answer, siblings, mode])
  const locked = state === 'right' || state === 'revealed'

  // Character scripts: spacing is a typing convenience, not the point of the
  // exercise — "나는밥을먹어요" counts as "나는 밥을 먹어요".
  const same = (a, b) => (CJK.has(lang) ? norm(a).replace(/\s+/g, '') === norm(b).replace(/\s+/g, '') : norm(a) === norm(b))
  const check = () => {
    if (same(val, item.a)) { setState('right'); onResult(true, val); say && say(item.a) }
    else { setState('wrong'); shake(inputRef.current); onResult(false, val) }
  }
  // Each press reveals one more letter (spaces are skipped automatically).
  const giveHint = () => setHint(h => { let n = h + 1; while (n < answer.length && /\s/.test(answer[n - 1])) n++; return Math.min(answer.length, n) })
  const scaffold = hint > 0 ? answer.slice(0, hint) + answer.slice(hint).replace(/[\p{L}\p{N}]/gu, '_') : null
  const addTile = (t) => setVal(v => (mode === 'word' ? (v ? `${v} ${t}` : t) : v + t))
  const backspace = () => setVal(v => (mode === 'word' ? v.replace(/\s*\S+\s*$/, '') : v.slice(0, -1)))

  return (
    <>
      <div className={styles.exQuestion}>
        {type === 'translate'
          ? item.q
          : <SpokenText text={item.q} lang={lang} voice={voice} rate={rate} showSpeaker={false} onWordClick={(w, e) => lookup(w, e, item.q)} />}
      </div>
      {scaffold && !locked && <div className={styles.scaffold}>{scaffold}</div>}
      <input ref={inputRef} className={`${styles.exInput} ${state === 'right' ? styles.inputRight : state === 'wrong' ? styles.inputWrong : ''}`}
        value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') check() }}
        placeholder="your answer…" spellCheck="false" autoComplete="off" disabled={locked} />
      {tilesOn && !locked && (
        <div className={styles.tileRow}>
          {tiles.map((t, i) => <button key={i} type="button" className={styles.tile} onClick={() => addTile(t)}>{t === ' ' ? '␣' : t}</button>)}
          <button type="button" className={styles.tileCtl} onClick={backspace} title="Delete">⌫</button>
          <button type="button" className={styles.tileCtl} onClick={() => setVal('')} title="Clear">clear</button>
        </div>
      )}
      <div className={styles.exActions}>
        {!locked && <button className={styles.checkBtn} onClick={check}>Check</button>}
        {!locked && hint < answer.length && <button className={styles.toolBtn} onClick={giveHint} title="Reveal the next letter">💡 Hint</button>}
        {!locked && <button className={`${styles.toolBtn} ${tilesOn ? styles.toolBtnActive : ''}`} onClick={() => setTilesOn(t => !t)} title="Build the answer by tapping tiles — handy for characters and special letters">⌨ Tiles</button>}
        {state === 'wrong' && <button className={styles.linkBtn} onClick={() => { setState('revealed'); setVal(item.a) }}>reveal</button>}
        {locked && (
          <span className={styles.answerTag}>
            ✓ <SpokenText text={item.a} lang={lang} voice={voice} rate={rate} onWordClick={(w, e) => lookup(w, e, item.a)} />
          </span>
        )}
      </div>
    </>
  )
}

function SpeakItem({ item, say, onResult }) {
  const [shown, setShown] = useState(false)
  return (
    <>
      <div className={styles.exQuestion}>{item.q}</div>
      {!shown
        ? <button className={styles.checkBtn} onClick={() => { setShown(true); onResult(true, null) }}>Reveal a model answer</button>
        : <div className={styles.modelAnswer} onClick={() => say(item.a)}><VolumeUpIcon /> {item.a}</div>}
    </>
  )
}

function MatchExercise({ ex, say, onComplete }) {
  const { lessonResult } = useLesson()
  const missed = useRef(new Set())
  const items = ex.items || []
  const left = items.map((it, i) => ({ i, text: it.q }))
  const right = useMemo(() => shuffle(items.map((it, i) => ({ i, text: it.a }))), [ex])
  const [selL, setSelL] = useState(null)
  const [pairs, setPairs] = useState({}) // leftIndex -> true
  const [wrong, setWrong] = useState(null)
  const rowRef = useRef(null)

  const tryPair = (rIdx) => {
    if (selL == null) return
    if (rIdx === selL) {
      setPairs(p => ({ ...p, [selL]: true })); say && say(items[selL].q)
      // Right only if matched without a wrong try first.
      if (lessonResult) lessonResult(items[selL].key || items[selL].q, !missed.current.has(selL))
      setSelL(null)
    }
    else { missed.current.add(selL); setWrong(rIdx); shake(rowRef.current); setTimeout(() => setWrong(null), 400) }
  }
  const allDone = Object.keys(pairs).length >= items.length && items.length > 0

  return (
    <div className={styles.exercise}>
      <div className={styles.exHead}><span className={styles.exType}>match</span><span className={styles.exPrompt}>{ex.prompt || 'Match the pairs'}</span></div>
      <div className={styles.matchGrid} ref={rowRef}>
        <div className={styles.matchCol}>
          {left.map(l => (
            <button key={l.i} disabled={pairs[l.i]} className={`${styles.matchTile} ${selL === l.i ? styles.matchSel : ''} ${pairs[l.i] ? styles.matchLocked : ''}`} onClick={() => setSelL(l.i)}>{l.text}</button>
          ))}
        </div>
        <div className={styles.matchCol}>
          {right.map(r => (
            <button key={r.i} disabled={pairs[r.i]} className={`${styles.matchTile} ${pairs[r.i] ? styles.matchLocked : ''} ${wrong === r.i ? styles.matchWrong : ''}`} onClick={() => tryPair(r.i)}>{r.text}</button>
          ))}
        </div>
      </div>
      {allDone && <button className={styles.doneBtn} onClick={onComplete}>Finish exercise ✓</button>}
    </div>
  )
}

// ── reading, dialogue, speaking ──────────────────────────────────────────────
const SPEEDS = [[0.6, 'Slow'], [0.8, '0.8×'], [1, '1×'], [1.2, 'Fast']]

function ReadingBlock({ reading, index, accent, onComplete }) {
  const { lang, langName, voice, rate, askModel } = useLesson()
  const { lookup } = useWordLookup()
  const [reveal, setReveal] = useState({})
  const [localRate, setLocalRate] = useState(rate)
  const [readingAll, setReadingAll] = useState(false)
  const [showCtx, setShowCtx] = useState(false)
  const [ctxText, setCtxText] = useState('')
  const [ctxLoading, setCtxLoading] = useState(false)
  const [info, setInfo] = useState({}) // question index -> { en, why, loadingEn, loadingWhy }
  const sentences = useMemo(() => String(reading?.text || '').split(/(?<=[.!?…”])\s+/).filter(Boolean), [reading])
  const refs = useRef([])
  const stopRef = useRef(false)

  // "Read all": chain the sentences so each lights up as it is read.
  const readAll = async () => {
    if (readingAll) { stopRef.current = true; refs.current.forEach(r => r && r.stop()); setReadingAll(false); return }
    stopRef.current = false
    setReadingAll(true)
    for (let i = 0; i < sentences.length; i++) {
      if (stopRef.current) break
      const r = refs.current[i]
      if (r) await r.play() // sequential on purpose: one sentence at a time
    }
    setReadingAll(false)
  }
  useEffect(() => () => { stopRef.current = true }, [])

  const contextHint = async () => {
    if (showCtx) { setShowCtx(false); return }
    setShowCtx(true)
    if (ctxText || ctxLoading) return
    setCtxLoading(true)
    try {
      setCtxText(await askModel(`You are a friendly ${langName} teacher. A beginner is about to read this ${langName} passage. In 2-3 short English sentences give helpful context: what it is about, who is in it, and ONE grammar point to notice. Do NOT translate the passage. Plain text, no preamble.\n\nPassage:\n${reading.text}`, { max: 220 }))
    } catch (e) { setCtxText(e.message || 'Could not reach the Mac Studio.') }
    setCtxLoading(false)
  }
  const patch = (i, p) => setInfo(s => ({ ...s, [i]: { ...(s[i] || {}), ...p } }))
  const answerEn = async (i, q) => {
    if (info[i]?.en || info[i]?.loadingEn) return
    patch(i, { loadingEn: true })
    try { patch(i, { en: await askModel(`Translate this short ${langName} answer into natural English. Reply with ONLY the translation.\nQuestion: ${q.q}\nAnswer: ${q.a}`, { max: 80 }) }) }
    catch (e) { patch(i, { en: e.message || 'Could not reach the Mac Studio.' }) }
    patch(i, { loadingEn: false })
  }
  const answerWhy = async (i, q) => {
    if (info[i]?.why || info[i]?.loadingWhy) return
    patch(i, { loadingWhy: true })
    try { patch(i, { why: await askModel(`You are a ${langName} teacher. In 2-3 short English sentences explain why "${q.a}" answers the question "${q.q}": quote the relevant words from the passage and briefly explain any ending, suffix or particle in the answer. Plain text, no preamble.\n\nPassage:\n${reading.text}`, { max: 220 }) }) }
    catch (e) { patch(i, { why: e.message || 'Could not reach the Mac Studio.' }) }
    patch(i, { loadingWhy: false })
  }

  return (
    <div className={styles.section} data-accent={accent}>
      <SectionHead index={index} title={reading?.title || 'Reading'} kind="reading" />

      <div className={styles.toolRow}>
        <button className={`${styles.toolBtn} ${readingAll ? styles.toolBtnActive : ''}`} onClick={readAll}>{readingAll ? '■ Stop' : '▶ Read all'}</button>
        <button className={`${styles.toolBtn} ${showCtx ? styles.toolBtnActive : ''}`} onClick={contextHint}>💡 Context</button>
        <span className={styles.speedChips}>
          {SPEEDS.map(([v, l]) => <button key={v} className={`${styles.speedChip} ${Math.abs(localRate - v) < 0.01 ? styles.speedChipActive : ''}`} onClick={() => setLocalRate(v)}>{l}</button>)}
        </span>
        <span className={styles.toolTip}>tap a word for its meaning</span>
      </div>
      {showCtx && <div className={styles.hintPanel}>{ctxLoading ? 'thinking…' : ctxText}</div>}

      <div className={styles.readingCard}>
        {sentences.map((s, i) => (
          <SpokenText key={i} ref={el => { refs.current[i] = el }} className={styles.readSentence} text={s} lang={lang} voice={voice} rate={localRate}
            onWordClick={(w, e) => lookup(w, e, s)} />
        ))}
      </div>

      {reading?.glossary?.length > 0 && (
        <div className={styles.glossBox}>
          <div className={styles.glossLabel}>A few useful words</div>
          <div className={styles.glossGrid}>
            {reading.glossary.map((g, i) => <span key={i} className={styles.glossItem} onClick={(e) => lookup(g.tr, e)}><b>{g.tr}</b> — {g.en}</span>)}
          </div>
        </div>
      )}

      {reading?.questions?.length > 0 && (
        <div className={styles.qCard}>
          <div className={styles.qLabel}>Comprehension — tap a question to reveal the answer</div>
          {reading.questions.map((q, i) => (
            <div key={i} className={styles.qItem}>
              <div className={styles.qRow} onClick={() => setReveal(r => ({ ...r, [i]: true }))}>
                <span className={styles.qText}>{i + 1}. {q.q}</span>
                {!reveal[i] && <span className={styles.qAns}>tap to reveal</span>}
              </div>
              {reveal[i] && (
                <div className={styles.qAnswer}>
                  <SpokenText className={styles.qAnsShown} text={q.a} lang={lang} voice={voice} rate={localRate} onWordClick={(w, e) => lookup(w, e, q.a)} />
                  <div className={styles.qTools}>
                    <button className={styles.linkBtn} onClick={() => answerEn(i, q)}>{info[i]?.loadingEn ? 'translating…' : 'in English'}</button>
                    <button className={styles.linkBtn} onClick={() => answerWhy(i, q)}>{info[i]?.loadingWhy ? 'thinking…' : 'explain'}</button>
                  </div>
                  {info[i]?.en && <div className={styles.qInfo}><b>EN</b> {info[i].en}</div>}
                  {info[i]?.why && <div className={styles.qInfo}><b>Why</b> {info[i].why}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <button className={styles.doneBtn} onClick={onComplete}>Mark read ✓</button>
    </div>
  )
}

function DialogueBlock({ dialogue, index, accent, onComplete }) {
  const { lang, voice, rate } = useLesson()
  const { lookup } = useWordLookup()
  const lines = dialogue?.lines || []
  return (
    <div className={styles.section} data-accent={accent}>
      <SectionHead index={index} title={dialogue?.title || 'Mini dialogue'} kind="dialogue" />
      <div className={styles.dialogueCard}>
        {lines.map((l, i) => (
          <div key={i} className={`${styles.dialLine} ${i % 2 ? styles.dialB : styles.dialA}`}>
            <span className={styles.dialWho}>{l.who}:</span>
            <span className={styles.dialBody}>
              <SpokenText className={styles.dialText} text={l.tr} lang={lang} voice={voice} rate={rate} onWordClick={(w, e) => lookup(w, e, l.tr)} />
              {l.en && <span className={styles.dialEn}>{l.en}</span>}
            </span>
          </div>
        ))}
      </div>
      <button className={styles.doneBtn} onClick={onComplete}>Practised ✓</button>
    </div>
  )
}

// Speaking: answer each prompt out loud. Speech-to-text turns it into text the
// learner can edit or re-record; for a saved lesson the answers are stored, so
// a teacher who set it as homework can read them and leave feedback.
function SpeakingBlock({ prompts, keys, index, accent, onComplete }) {
  const { lang, voice, rate, recordAnswer, saveWork, savedAnswers, savedAt, saveErr, teacherNote, canSave } = useLesson()
  const list = prompts || []
  // What each answer is saved under: the prompt's original wording.
  const keyOf = (i) => String((keys && keys[i]) || list[i] || '').trim()
  const [answers, setAnswers] = useState(() => list.map(() => ''))
  const [activeIdx, setActiveIdx] = useState(null)
  const [saving, setSaving] = useState(false)
  // Prefill from what was saved before, matched on the prompt rather than the
  // position — the submission now holds exercise answers too, so an item's
  // index no longer identifies it.
  // `list` is a fresh array every render, so the prompts are joined into a
  // stable string to depend on — no disable comment, no re-run every render.
  const promptKey = list.map((_, i) => keyOf(i)).join('\u0001')
  useEffect(() => {
    if (!savedAnswers || savedAnswers.size === 0) return
    const ps = promptKey ? promptKey.split('\u0001') : []
    setAnswers(prev => prev.map((v, i) => v || savedAnswers.get(String(ps[i] || '').trim()) || ''))
  }, [savedAnswers, promptKey])

  const setAnswer = (i, text) => {
    setAnswers(a => a.map((v, k) => (k === i ? text : v)))
    recordAnswer(keyOf(i), text)
  }
  const stt = useSpeechToText(lang, {
    onFinal: (t) => {
      if (activeIdx == null) return
      setAnswers(a => a.map((v, i) => {
        if (i !== activeIdx) return v
        const next = v ? `${v} ${t}` : t
        recordAnswer(keyOf(i), next)
        return next
      }))
    },
  })
  const record = (i) => {
    if (stt.listening && activeIdx === i) { stt.stop(); return }
    setActiveIdx(i)
    stt.start()
  }
  const rerecord = (i) => { setAnswer(i, ''); setActiveIdx(i); stt.start() }
  const edit = (i, v) => setAnswer(i, v)
  // Sends the whole lesson — every exercise answer as well as these — because
  // the submission is replaced wholesale, not appended to.
  const save = async () => {
    setSaving(true)
    try { await saveWork(); onComplete() } catch { /* surfaced via saveErr */ }
    setSaving(false)
  }
  const answered = answers.filter(a => a && a.trim()).length

  return (
    <div className={styles.section} data-accent={accent}>
      <SectionHead index={index} title="Answer out loud" kind="speaking" />
      <p className={styles.note}>
        {stt.supported
          ? 'Tap the mic, answer in a sentence or two, then tap again to stop. Your words appear as text — fix anything the recogniser misheard, or re-record.'
          : 'Speech recognition is not available in this browser — type your answers instead.'}
        {canSave ? ' Sending your work shares these answers AND everything you did in the practice sections with the teacher who set this lesson.' : ''}
      </p>

      {list.map((p, i) => {
        const live = stt.listening && activeIdx === i
        return (
          <div key={i} className={styles.speakItemCard}>
            <div className={styles.speakPrompt}><span className={styles.speakNum}>{i + 1}</span>{p}</div>
            <textarea className={styles.speakText} rows={2} value={answers[i] || ''} onChange={e => edit(i, e.target.value)}
              placeholder={stt.supported ? 'Your spoken answer will appear here…' : 'Type your answer…'} spellCheck="false" />
            {live && stt.interim && <div className={styles.speakInterim}>{stt.interim}…</div>}
            <div className={styles.speakActions}>
              {stt.supported && (
                <button className={`${styles.micBtn} ${live ? styles.micOn : ''}`} onClick={() => record(i)}>
                  {live ? '■ Stop' : (answers[i] ? '🎤 Add more' : '🎤 Record')}
                </button>
              )}
              {stt.supported && answers[i] && !live && <button className={styles.toolBtn} onClick={() => rerecord(i)} title="Clear and record again">↻ Re-record</button>}
              {answers[i] && <button className={styles.toolBtn} onClick={() => speak(answers[i], voice, rate)} title="Hear your answer read back">🔊 Hear it</button>}
            </div>
          </div>
        )
      })}
      {stt.error && <div className={styles.errNote}>{stt.error}</div>}

      {teacherNote?.comment && (
        <div className={styles.teacherNote}><b>Teacher</b> {teacherNote.comment}{teacherNote.reviewedAt ? <span className={styles.savedNote}> · {new Date(teacherNote.reviewedAt).toLocaleDateString()}</span> : null}</div>
      )}

      <div className={styles.speakFooter}>
        {canSave
          ? <button className={styles.doneBtn} onClick={save} disabled={saving}>{saving ? 'Saving…' : `Send my work to the teacher (${answered}/${list.length} spoken) ✓`}</button>
          : <button className={styles.doneBtn} onClick={onComplete}>Done ✓</button>}
        {savedAt && !saveErr && <span className={styles.savedNote}>Saved {new Date(savedAt).toLocaleString()}</span>}
        {saveErr && <span className={styles.errNote}>{saveErr}</span>}
      </div>
    </div>
  )
}

// Finishing a Skill Tree lesson: keep the answers or not, and share them with
// which of my teachers for this language. Nothing is saved or shared without it.
function FinishChoice({ lang, langName, previous, meetingId, answers, onCancel, onDone }) {
  const { data } = useQuery(MY_TEACHER_LINKS_QUERY, { fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const teachers = useMemo(() => (data?.myTeacherLinks || []).filter(l => (l.langs || []).includes(lang)).map(l => l.username).sort(), [data, lang])
  const [keep, setKeep] = useState(true)
  const [share, setShare] = useState(() => new Set(previous?.sharedWith || []))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [save] = useMutation(SAVE_LESSON_WORK_MUTATION)
  const [remove] = useMutation(DELETE_LESSON_WORK_MUTATION)
  const toggle = (t) => setShare(s => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n })
  const done = async () => {
    setBusy(true); setErr('')
    try {
      if (keep) await save({ variables: { meetingId, answers, shareWith: teachers.filter(t => share.has(t)) } })
      else if (previous) await remove({ variables: { meetingId } })
    } catch (e) {
      // Stay open: closing would look like the choice had gone through.
      setErr(e.message || 'Could not save — your work is still here.'); setBusy(false); return
    }
    await onDone()
  }
  const savedOn = previous ? new Date(previous.updatedAt || previous.savedAt).toLocaleDateString() : ''
  return (
    <div className={styles.choiceBackdrop} onClick={busy ? undefined : onCancel}>
      <div className={styles.choiceCard} role="dialog" aria-label="Finish the lesson" onClick={e => e.stopPropagation()}>
        <h3 className={styles.choiceTitle}>Finish the lesson</h3>
        <label className={styles.choiceRow}>
          <input type="checkbox" checked={keep} onChange={e => setKeep(e.target.checked)} />
          <span>Save my answers</span>
        </label>
        <div className={styles.choiceHint}>
          {keep ? `${answers.length} answer${answers.length === 1 ? '' : 's'}, kept with this lesson on your account.`
            : previous ? `The answers you saved on ${savedOn} will be deleted.` : "Your answers won't be kept."}
        </div>
        {keep && (
          <>
            <div className={styles.choiceLabel}>Share with your teacher</div>
            {!data ? <div className={styles.choiceHint}>Loading…</div>
              : teachers.length === 0
                ? <div className={styles.choiceHint}>None of your teachers teaches you {langName} yet. Add one under Homework → My teachers to share lessons like this.</div>
                : teachers.map(t => (
                  <label key={t} className={styles.choiceRow}>
                    <input type="checkbox" checked={share.has(t)} onChange={() => toggle(t)} />
                    <span>{t}</span>
                  </label>
                ))}
            {teachers.length > 0 && <div className={styles.choiceHint}>{teachers.some(t => share.has(t)) ? "They'll see your answers and can leave you a note." : 'Only you will see them.'}</div>}
          </>
        )}
        {err && <div className={styles.errNote}>{err}</div>}
        <div className={styles.choiceButtons}>
          <button className={styles.navBtn} onClick={onCancel} disabled={busy}>Keep working</button>
          <button className={`${styles.navBtn} ${styles.navBtnPrimary}`} onClick={done} disabled={busy}>{busy ? 'Saving…' : 'Complete ✓'}</button>
        </div>
      </div>
    </div>
  )
}
